import { isExactly } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Exactly — cross-margin fixed-rate pools on a 4-week maturity grid.
 *
 * Three facts the generic builder cannot derive:
 *  1. Early repayment is a REBATE, not a fee (the only lender of ours where
 *     that is true), and the rebate can be zero.
 *  2. Overdue debt accrues a ~164 %/yr LINEAR penalty on face value which
 *     feeds the health factor, so an overdue position drifts into liquidation
 *     on its own.
 *  3. The lender-side early exit is a deterministic haircut formula
 *     (`face / (1 + rate(remaining))`), not a market price.
 */
export const exactlyAdapter: TermAdapter = {
  id: 'exactly',
  matches: isExactly,
  profileId: () => 'exactly.fixed@v1',
  build: (input) => ({
    supply: {
      // Fixed deposits have no late penalty and no expiry — an unclaimed
      // matured deposit simply stops earning.
      maturity: { atMaturity: 'stops-earning' },
      exit: {
        mode: 'market-sale',
        settlement: 'sync',
        // `withdrawAtMaturity` before maturity pays face / (1 + curve rate),
        // so the cost is deterministic but worsens as rates rise.
        priceRisk: 'haircut-formula',
        partialAllowed: true,
      },
      counterparty: { kind: 'pool', solvency: 'overcollateralized' },
    },
    borrow: {
      debtShape: 'static-face',
      exit: {
        earlyRepay: 'discount',
        atMaturityCost: 'face',
        lateBehaviour: 'penalty-accrues',
        partialAllowed: true,
      },
      maturity: { atMaturity: 'penalty-accrues' },
      liquidation: {
        // Price-based, but coupled to time through the penalty above.
        trigger: 'price-and-time',
        seizure: 'proportional',
      },
      counterparty: { kind: 'pool', solvency: 'overcollateralized' },
    },
    constraints: {
      // Exactly reuses `loanId` as the MATURITY, stringified — not a loan
      // counter. This is the trap the field exists to defuse.
      positionModel: 'loan-id',
      positionIdMeaning:
        'The unix maturity timestamp of the fixed pool, as a string.',
    },
  }),
}
